import { useCallback, useEffect, useRef, useState } from "react";
import PartySocket from "partysocket";

// Host van je eigen Cloudflare Worker — na `npx wrangler deploy` krijg je een
// URL als "wyckoffcafe-live.<jouw-cloudflare-subdomain>.workers.dev".
// Zet 'm in .env als REACT_APP_PARTY_HOST, of laat de fallback staan tijdens
// lokaal ontwikkelen (`npx wrangler dev` draait dan op localhost:8787).
const PARTYKIT_HOST =
  process.env.REACT_APP_PARTY_HOST || "127.0.0.1:8787";

// Vangnet voor room-geschiedenis die al dubbele berichten bevat (opgeslagen
// vóór de server-side dedupe-fix): filtert opeenvolgende exacte duplicaten
// (zelfde afzender+tekst binnen 2s) uit de initiële chatlog.
function dedupeChatHistory(list) {
  const out = [];
  for (const e of list) {
    const last = out[out.length - 1];
    const isDup =
      last && last.from === e.from && last.text === e.text && Math.abs((e.ts || 0) - (last.ts || 0)) < 2000;
    if (!isDup) out.push(e);
  }
  return out;
}

/**
 * useLiveSession — verbindt met een PartyKit room en synchroniseert:
 *   - de gedeelde cycleStore (SC/AR/ST/... annotaties), bidirectioneel
 *   - een chatlog
 *
 * cycleStore moet een object zijn zoals geretourneerd door useCycleStore(),
 * met in elk geval: { broadcastRef, applyRemoteCycles }.
 */
export function useLiveSession({ room, name, role, cycleStore }) {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [chat, setChat] = useState([]);
  const [participants, setParticipants] = useState({}); // id -> {name, role}
  const socketRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (socketRef.current || !room || !name) return;
    setConnecting(true);

    const socket = new PartySocket({ host: PARTYKIT_HOST, room });
    socketRef.current = socket;

    socket.addEventListener("open", () => {
      setConnected(true);
      setConnecting(false);
      // Zorg dat de ref weer naar déze (levende) socket wijst — relevant na
      // een automatische reconnect (zie de close-handler hieronder), waar
      // de ref expres niet werd weggegooid, maar voor de zekerheid.
      socketRef.current = socket;
      socket.send(JSON.stringify({ type: "hello", name, role }));

      // Heartbeat: zonder verkeer kunnen tussenliggende netwerklagen (edge,
      // browser-shields, router/firewall) een stille WebSocket na een tijdje
      // zelf dichtgooien, ook al staat de Durable Object nog gewoon open
      // (hibernation houdt alléén de server-kant netjes slapend, dat
      // voorkomt geen idle-timeouts elders op het pad). Door elke 25s een
      // klein pingetje te sturen blijft er altijd verkeer lopen en valt de
      // verbinding niet meer stil weg na een rustige periode in de chat.
      // Eerst een eventuele oude interval opruimen (bv. na een automatische
      // reconnect vuurt "open" opnieuw — zonder dit zouden er meerdere
      // intervals naast elkaar blijven lopen).
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(() => {
        if (socket.readyState === socket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 25000);
      // Vanaf nu broadcast elke lokale cycle-wijziging naar de room.
      cycleStore.broadcastRef.current = (cycles) => {
        socket.send(JSON.stringify({ type: "cycles", cycles }));
      };
      // Als je al eigen tekeningen hebt staan (bv. coach die een reeds
      // voorbereide chart verbindt), deel die meteen met de room — anders
      // zou alleen een latere wijziging ze zichtbaar maken voor de ander.
      if (cycleStore.getCurrentCycles) {
        const current = cycleStore.getCurrentCycles();
        if (current && current.length) {
          socket.send(JSON.stringify({ type: "cycles", cycles: current }));
        }
      }
    });

    socket.addEventListener("message", (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (msg.type) {
        case "state":
          // Alleen toepassen als de room daadwerkelijk iets te bieden heeft.
          // Een VERSE room stuurt hier een lege array — die mag nooit eigen,
          // al aanwezige tekeningen wegvegen (zie ook getCurrentCycles
          // hierboven, dat zo'n leeg-room-scenario juist de andere kant op
          // oplost door lokale tekeningen meteen te delen).
          if (msg.cycles && msg.cycles.length) cycleStore.applyRemoteCycles(msg.cycles);
          // BUG (nu gefixt): een lege array is in JS ook "truthy", dus
          // `if (msg.chat)` liet een leeg chat-array van de server gewoon
          // de lokale chatgeschiedenis overschrijven — bij elke (her)connect,
          // inclusief de nu stillere/vaker voorkomende automatische
          // reconnects. Zelfde patroon als de cycles-regel hierboven: alleen
          // toepassen als er ook echt iets in zit.
          if (msg.chat && msg.chat.length) setChat(dedupeChatHistory(msg.chat));
          break;
        case "cycles":
          cycleStore.applyRemoteCycles(msg.cycles || []);
          break;
        case "chat": {
          const e = msg.entry;
          setChat((prev) => {
            // Dedupe als vangnet: negeer een exact gelijk bericht dat kort
            // na het vorige binnenkomt (reconnect/hibernation-replay).
            const last = prev[prev.length - 1];
            const isDup =
              last &&
              last.from === e.from &&
              last.text === e.text &&
              Math.abs((e.ts || 0) - (last.ts || 0)) < 2000 &&
              (e.id ? e.id === last.id : true);
            if (isDup) return prev;
            return [...prev.slice(-199), e];
          });
          break;
        }
        case "presence":
          setParticipants((prev) => {
            const next = { ...prev };
            if (msg.left) delete next[msg.id];
            else next[msg.id] = { name: msg.name, role: msg.role };
            return next;
          });
          break;
        default:
          break;
      }
    });

    socket.addEventListener("close", () => {
      setConnected(false);
      setParticipants({});

      // BELANGRIJK: PartySocket (ReconnectingWebSocket) probeert bij een
      // onbedoelde drop (netwerkhapering, edge-timeout, enz.) ZELF
      // automatisch opnieuw te verbinden — "open" vuurt dan vanzelf weer.
      // Eerder gooiden we hier meteen socketRef.current en broadcastRef
      // weg, waardoor versturen (sendChat/broadcast) voorgoed kapot bleef
      // nadat de library allang weer stilletjes verbonden was: de UI toonde
      // soms zelfs "LIVE", maar er kwam nooit meer iets door zonder
      // handmatig op "leave" en dan "Connect" te klikken. `shouldReconnect`
      // vertelt ons of dít een tijdelijke drop is (dan niets opruimen, en
      // gewoon wachten op de automatische "open") of een bewuste/definitieve
      // disconnect() (dan pas écht alles opruimen).
      if (socket.shouldReconnect) {
        setConnecting(true);
        return;
      }

      setConnecting(false);
      cycleStore.broadcastRef.current = null;
      socketRef.current = null;
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    });

    socket.addEventListener("error", () => {
      setConnecting(false);
    });
  }, [room, name, role, cycleStore]);

  const disconnect = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
    cycleStore.broadcastRef.current = null;
    setConnected(false);
    setParticipants({});
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, [cycleStore]);

  const sendChat = useCallback(
    (text) => {
      if (!socketRef.current || !text.trim()) return;
      socketRef.current.send(JSON.stringify({ type: "chat", from: name, text }));
    },
    [name]
  );

  // Netjes opruimen als het component unmount terwijl er nog verbinding is.
  useEffect(() => {
    return () => {
      socketRef.current?.close();
      socketRef.current = null;
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, []);

  return {
    connected,
    connecting,
    chat,
    participants,
    connect,
    disconnect,
    sendChat,
  };
}
